import { TRUTO_EXPLORER_SINGLE_PAGE_ARG } from '@/lib/truto-list-page'

export type TrutoExplorerResource = {
  api: 'unified' | 'proxy'
  model?: string
  resource: string
}

const UNIFIED_PREFIX = 'unified:'
const PROXY_PREFIX = 'proxy:'

export function parseTrutoExplorerResource(encoded: string): TrutoExplorerResource | null {
  if (!encoded) return null
  if (encoded.startsWith(UNIFIED_PREFIX)) {
    const rest = encoded.slice(UNIFIED_PREFIX.length)
    const slash = rest.indexOf('/')
    if (slash <= 0) return null
    return {
      api: 'unified',
      model: rest.slice(0, slash),
      resource: rest.slice(slash + 1),
    }
  }
  if (encoded.startsWith(PROXY_PREFIX)) {
    const resource = encoded.slice(PROXY_PREFIX.length)
    if (!resource) return null
    return { api: 'proxy', resource }
  }
  return null
}

export function formatTrutoExplorerResource(parsed: TrutoExplorerResource): string {
  if (parsed.api === 'unified') {
    if (!parsed.model || !parsed.resource) throw new Error('unified resource requires model and resource')
    return `${UNIFIED_PREFIX}${parsed.model}/${parsed.resource}`
  }
  return `${PROXY_PREFIX}${parsed.resource}`
}

export type TrutoHttpVerb = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

/** Explorer methods that can be invoked against a unified/proxy resource. */
export type TrutoRequestMethod = 'list' | 'get' | 'create' | 'update' | 'delete' | 'custom'

/** Map an explorer CRUD method to the HTTP verb Truto uses for it. */
export function trutoHttpVerb(method: string): TrutoHttpVerb {
  switch (method) {
    case 'create':
      return 'POST'
    case 'update':
      return 'PATCH'
    case 'delete':
      return 'DELETE'
    case 'list':
    case 'get':
    default:
      return 'GET'
  }
}

const TRUTO_BODY_VERBS = new Set<TrutoHttpVerb>(['POST', 'PUT', 'PATCH', 'DELETE'])

export type BuildTrutoRequestOptions = {
  parsed: TrutoExplorerResource | null
  integratedAccountId: string
  /** One of list/get/create/update/delete, or 'custom' for the proxy custom API. */
  method: TrutoRequestMethod | string
  id?: string
  query?: Record<string, unknown>
  body?: Record<string, unknown>
  /** Required when method === 'custom': arbitrary path + HTTP verb → proxy_custom. */
  custom?: { path: string; verb: string }
}

/**
 * Build the connector `{ method, args }` for any Truto Data Explorer request:
 * unified + proxy CRUD, plus the Custom API (arbitrary path/verb → proxy_custom).
 * Supersedes buildTrutoListMethodArgs / buildTrutoWriteMethodArgs, which are
 * kept as thin wrappers below.
 */
export function buildTrutoRequest(
  opts: BuildTrutoRequestOptions,
): { method: string; args: Record<string, unknown> } {
  const { parsed, integratedAccountId, method, id, query, body, custom } = opts

  if (method === 'custom') {
    if (!custom || !custom.path.trim()) throw new Error('Custom API requires a path')
    const verb = (custom.verb || 'GET').toUpperCase() as TrutoHttpVerb
    const args: Record<string, unknown> = {
      resource: custom.path.trim().replace(/^\//, ''),
      method: verb,
      integrated_account_id: integratedAccountId,
    }
    if (query && Object.keys(query).length) args.query = query
    if (body && Object.keys(body).length && TRUTO_BODY_VERBS.has(verb)) args.data = body
    return { method: 'proxy_custom', args }
  }

  if (!parsed) throw new Error('Invalid Truto resource')

  const prefix = parsed.api === 'unified' ? '' : 'proxy_'
  const base: Record<string, unknown> = {
    resource: parsed.resource,
    integrated_account_id: integratedAccountId,
  }
  if (parsed.api === 'unified') {
    if (!parsed.model) throw new Error('unified resource requires model')
    base.unified_model = parsed.model
  }
  const withQuery = query && Object.keys(query).length ? { query } : {}

  switch (method) {
    case 'list':
      return {
        method: `${prefix}list`,
        args: { ...base, query: query ?? {}, [TRUTO_EXPLORER_SINGLE_PAGE_ARG]: true },
      }
    case 'get':
      if (!id) throw new Error('get requires an id')
      return { method: `${prefix}get`, args: { ...base, id, ...withQuery } }
    case 'create':
      return { method: `${prefix}create`, args: { ...base, data: body ?? {}, ...withQuery } }
    case 'update':
      if (!id) throw new Error('update requires an id')
      return { method: `${prefix}update`, args: { ...base, id, data: body ?? {}, ...withQuery } }
    case 'delete':
      if (!id) throw new Error('delete requires an id')
      return { method: `${prefix}delete`, args: { ...base, id, ...withQuery } }
    default:
      throw new Error(`Unsupported Truto method: ${method}`)
  }
}

export const DEFAULT_TRUTO_BASE_URL = 'https://api.truto.one'

export type BuildTrutoRequestUrlOptions = {
  parsed: TrutoExplorerResource | null
  integratedAccountId: string
  method: TrutoRequestMethod | string
  id?: string
  query?: Record<string, unknown>
  custom?: { path: string; verb: string }
  baseUrl?: string
  /**
   * Cardamom/catalog connectors call the vendor API directly (no Truto host).
   * Renders a vendor-relative preview: no base URL, no /proxy prefix, and no
   * integrated_account_id param (connection context, not a vendor query param).
   */
  vendorRelative?: boolean
}

/**
 * Build an indicative request URL for the preview bar — mirrors the Truto app's
 * get(Unified)ApiUrlForResource / getCustomApiUrl helpers. `baseUrl` defaults to
 * the public API host since the explorer doesn't receive the connector base_url.
 */
export function buildTrutoRequestUrl(opts: BuildTrutoRequestUrlOptions): string {
  const { parsed, integratedAccountId, method, id, query, custom, vendorRelative } = opts
  const base = vendorRelative ? '' : (opts.baseUrl || DEFAULT_TRUTO_BASE_URL).replace(/\/+$/, '')

  let path: string
  if (method === 'custom') {
    const clean = (custom?.path || '').trim().replace(/^\//, '')
    if (!clean) return ''
    path = vendorRelative ? `/${clean}` : `/custom/${clean}`
  } else {
    if (!parsed) return ''
    const segment = parsed.api === 'unified' ? `${parsed.model}/${parsed.resource}` : parsed.resource
    const prefix = vendorRelative ? '' : parsed.api === 'unified' ? '/unified' : '/proxy'
    if (method === 'get' || method === 'update' || method === 'delete') {
      path = `${prefix}/${segment}/${id || ':id'}`
    } else if (method === 'list' || method === 'create') {
      path = `${prefix}/${segment}`
    } else {
      path = `${prefix}/${segment}/${method}`
    }
  }

  const params = new URLSearchParams()
  if (!vendorRelative) params.set('integrated_account_id', integratedAccountId)
  appendTrutoQueryParams(params, query)
  const qs = params.toString()
  const url = `${base}${path}${qs ? `?${qs}` : ''}`
  return vendorRelative ? url.replace(/^\//, '') : url
}

/**
 * Serialize a Truto query object into URL params, mirroring the SDK's `qs`
 * bracket syntax: nested operator objects (`{ created_at: { gte: x } }`) become
 * `created_at[gte]=x`, and arrays are comma-joined (`id[in]=a,b`).
 */
function appendTrutoQueryParams(params: URLSearchParams, query?: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(','))
      continue
    }
    if (typeof value === 'object') {
      for (const [op, v] of Object.entries(value as Record<string, unknown>)) {
        if (v === undefined || v === null || v === '') continue
        params.set(`${key}[${op}]`, Array.isArray(v) ? v.join(',') : String(v))
      }
      continue
    }
    params.set(key, String(value))
  }
}

export type TrutoWriteOp = 'create' | 'update' | 'delete'

/** Thin wrapper over buildTrutoRequest for unified/proxy list calls. */
export function buildTrutoListMethodArgs(
  parsed: TrutoExplorerResource,
  integratedAccountId: string,
  query: Record<string, unknown> = {},
): { method: string; args: Record<string, unknown> } {
  return buildTrutoRequest({ parsed, integratedAccountId, method: 'list', query })
}

/** Thin wrapper over buildTrutoRequest for unified/proxy create/update/delete. */
export function buildTrutoWriteMethodArgs(
  parsed: TrutoExplorerResource,
  integratedAccountId: string,
  op: TrutoWriteOp,
  opts: { id?: string; data?: Record<string, unknown> } = {},
): { method: string; args: Record<string, unknown> } {
  return buildTrutoRequest({ parsed, integratedAccountId, method: op, id: opts.id, body: opts.data })
}

export function trutoAccountDisplayLabel(account: {
  label?: string | null
  tenant_id: string
}): string {
  if (account.label?.trim()) return account.label.trim()
  return account.tenant_id
}
